import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, Package, CreditCard, MapPin, Calendar, User, ShoppingCart, Clock, CheckCircle, XCircle, Download, Store } from 'lucide-react';
import api from '../services/api';
import { Button, Input, Select } from '../components/ui';

interface OrderItem {
  _id?: string;
  id?: string;
  product: {
    id: string;
    name: string;
    price: number;
    images?: string[];
  };
  quantity: number;
  price: number;
  size?: string;
  color?: string;
}

interface Payment {
  id: string;
  transactionId: string;
  paymentMethod: 'MPESA' | 'ORANGE_MONEY' | 'AIRTEL_MONEY' | 'CASH_ON_DELIVERY';
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'REFUNDED' | 'CANCELLED';
  amount: number;
  providerTransactionId?: string;
  createdAt: string;
}

interface Payout {
  _id?: string;
  id?: string;
  seller_id: string;
  order_id: string;
  amount_paid: number;
  payout_date: string;
  status: 'PENDING' | 'PROCESSED' | 'FAILED';
  transaction_id?: string;
  notes?: string;
}

interface Order {
  id?: string;
  _id?: string;
  orderNumber: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  items: OrderItem[];
  shippingAddress: {
    fullName: string;
    phoneNumber: string;
    homeAddress: string;
    city: string;
    state: string;
    country: string;
    postalCode: string;
  };
  paymentMethod: string;
  paymentStatus: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'REFUNDED';
  orderStatus: 'PENDING' | 'CONFIRMED' | 'OUT_FOR_DELIVERY' | 'COMPLETE' | 'CANCELLED';
  totalPrice: number;
  shippingFee: number;
  finalPrice: number;
  paymentReference?: string;
  paymentMarkedPaidAt?: string;
  notes?: string;
  payments?: Payment[]; // Array of payment records
  activePayment?: Payment; // Current active payment
  payouts?: Payout[]; // Array of payout records (created when order is COMPLETE)
  createdAt: string;
  updatedAt: string;
  // Seller & Shop (from backend)
  seller_id?: string | null;
  seller_name?: string | null;
  seller_email?: string | null;
  shop_id?: string | null;
  shop_name?: string | null;
  shop_logo?: string | null;
}

interface OrdersResponse {
  orders: Order[];
  total: number;
  totalPages: number;
  currentPage: number;
}

interface OrderStats {
  totalOrders: number;
  processingOrders: number;
  completedOrders: number;
  cancelledOrders: number;
}

const Orders: React.FC = () => {
  const { t } = useTranslation();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus] = useState('');
  const [filterPaymentStatus] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [stats, setStats] = useState<OrderStats>({
    totalOrders: 0,
    processingOrders: 0,
    completedOrders: 0,
    cancelledOrders: 0
  });
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [markingPaidId, setMarkingPaidId] = useState<string | null>(null);
  const productOwnerMapRef = React.useRef<Record<string, { _id: string; name?: string }>>({});
  const sellerIdToOwnerNameRef = React.useRef<Record<string, string>>({});

  /** Existing endpoint: GET admin/products. Builds productId -> owner (seller id + shop name). */
  const fetchProductOwnerMap = useCallback(async (neededIds: string[]): Promise<Record<string, { _id: string; name?: string }>> => {
    const map = { ...productOwnerMapRef.current };
    const missing = neededIds.filter((id) => !map[id]);
    if (missing.length === 0) return map;
    let page = 1;
    const limit = 100;
    let totalPages = 1;
    do {
      const res = await api.get(`/admin/products?page=${page}&limit=${limit}`);
      const data = res.data?.data ?? res.data ?? {};
      const products: any[] = Array.isArray(data.products) ? data.products : [];
      totalPages = Number(data.totalPages) ?? 1;
      for (const p of products) {
        const pid = p.id ?? p._id?.toString?.() ?? p._id;
        const owner = p.owner;
        if (pid && owner) {
          const oid = owner._id?.toString?.() ?? owner.id ?? owner;
          const oname = owner.name ?? null;
          if (oid) map[pid] = { _id: oid, name: oname ?? undefined };
        }
      }
      productOwnerMapRef.current = map;
      const hasAll = missing.every((id) => map[id]);
      if (hasAll) break;
      page += 1;
    } while (page <= totalPages);
    return map;
  }, []);

  /** Existing endpoint: GET admin/users. Builds sellerId -> shop owner's full name (user.firstName + lastName). */
  const fetchSellerIdToOwnerName = useCallback(async (sellerIds: string[]): Promise<Record<string, string>> => {
    const map = { ...sellerIdToOwnerNameRef.current };
    const missing = sellerIds.filter((id) => !map[id]);
    if (missing.length === 0) return map;
    let page = 1;
    const limit = 100;
    let totalPages = 1;
    do {
      const res = await api.get(`/admin/users?page=${page}&limit=${limit}`);
      const data = res.data?.data ?? res.data ?? {};
      const users: any[] = Array.isArray(data.users) ? data.users : [];
      totalPages = Number(data.totalPages) ?? 1;
      for (const u of users) {
        const seller = u.seller;
        const sellerId = seller?._id?.toString?.() ?? seller?.id ?? (typeof seller === 'string' ? seller : null);
        if (sellerId) {
          const fullName = [u.firstName, u.lastName].filter(Boolean).join(' ').trim();
          if (fullName) map[sellerId] = fullName;
        }
      }
      sellerIdToOwnerNameRef.current = map;
      const hasAll = missing.every((id) => map[id]);
      if (hasAll) break;
      page += 1;
    } while (page <= totalPages);
    return map;
  }, []);

  const fetchOrderStats = async () => {
    try {
      const response = await api.get('/admin/orders/stats');
      const statsData = response.data.data;
      setStats({
        totalOrders: statsData.total || 0,
        processingOrders: (statsData.confirmed || 0) + (statsData.outForDelivery || 0),
        completedOrders: statsData.complete || 0,
        cancelledOrders: statsData.cancelled || 0
      });
    } catch (error) {
      console.error('Error fetching order stats:', error);
      setStats({
        totalOrders: 0,
        processingOrders: 0,
        completedOrders: 0,
        cancelledOrders: 0,
      });
    }
  };

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: entriesPerPage.toString(),
        ...(searchTerm && { search: searchTerm }),
        ...(filterStatus && { status: filterStatus }),
        ...(filterPaymentStatus && { paymentStatus: filterPaymentStatus }),
      });

      const response = await api.get(`/admin/orders?${params}`);
      const data: OrdersResponse = response.data.data;

      // Seller/shop display: use only existing backend endpoints. Prefer order response, then fallbacks.
      // 1) GET admin/orders – use seller_id, seller_name, shop_name, shop_logo when present.
      // 2) If order has items[].product.owner (from backend populate) – use it for shop name/logo.
      // 3) Fallback: GET admin/products (product.owner = shop/seller id + name), GET admin/users (seller_id → owner person name).
      let normalizedOrders = data.orders.map((order: any) => {
        const base = { ...order, id: order.id || order._id || (order as any).id };
        if (base.seller_name != null && base.shop_name != null) return base;
        const firstItem = base.items?.[0];
        const owner = firstItem?.product?.owner;
        if (owner && typeof owner === 'object') {
          const sid = owner._id?.toString?.() ?? owner.id ?? (typeof owner === 'string' ? owner : null);
          const shopName = owner.name ?? null;
          const slogo = owner.logo ?? null;
          if (sid && !base.seller_id) base.seller_id = sid;
          if (shopName && base.shop_name == null) base.shop_name = shopName;
          if (slogo && base.shop_logo == null) base.shop_logo = slogo;
        }
        return base;
      });

      const needProductIds = normalizedOrders
        .filter((o: any) => !o.shop_name && o.items?.[0]?.product)
        .map((o: any) => o.items[0].product._id ?? o.items[0].product.id)
        .filter(Boolean);
      if (needProductIds.length > 0) {
        try {
          const ownerMap = await fetchProductOwnerMap(needProductIds);
          const sellerIds = Array.from(new Set(Object.values(ownerMap).map((o) => o._id).filter(Boolean)));
          const ownerNameMap = sellerIds.length > 0 ? await fetchSellerIdToOwnerName(sellerIds) : {};
          normalizedOrders = normalizedOrders.map((order: any) => {
            if (order.shop_name != null) return order;
            const firstProductId = order.items?.[0]?.product?._id ?? order.items?.[0]?.product?.id;
            const owner = firstProductId ? ownerMap[firstProductId] : null;
            if (!owner) return order;
            const shopName = owner.name ?? null;
            const sellerName = owner._id ? (ownerNameMap[owner._id] ?? null) : null;
            return {
              ...order,
              seller_id: owner._id,
              seller_name: sellerName,
              shop_name: shopName,
            };
          });
        } catch (e) {
          console.warn('Could not fetch product owners for seller display:', e);
        }
      }
      // Seller name = shop owner (person). Fetch when missing or when it equals shop name (backend often sends shop name as seller name).
      const needSellerNames = Array.from(new Set(
        normalizedOrders
          .filter((o: any) => o.seller_id && (!o.seller_name || o.seller_name === o.shop_name))
          .map((o: any) => o.seller_id)
      ));
      if (needSellerNames.length > 0) {
        try {
          const ownerNameMap = await fetchSellerIdToOwnerName(needSellerNames);
          normalizedOrders = normalizedOrders.map((order: any) => {
            if (!order.seller_id) return order;
            const currentIsSameAsShop = order.seller_name && order.seller_name === order.shop_name;
            if (order.seller_name && !currentIsSameAsShop) return order;
            const name = ownerNameMap[order.seller_id];
            return name ? { ...order, seller_name: name } : order;
          });
        } catch (e) {
          console.warn('Could not fetch seller names for display:', e);
        }
      }

      setOrders(normalizedOrders);
      setTotal(data.total);
      setTotalPages(data.totalPages);
    } catch (error: any) {
      console.error('Error fetching orders:', error);
      // Don't show alert for 401 errors (handled by interceptor)
      if (error.response?.status !== 401) {
        // Optionally show a user-friendly error message
        console.warn('Failed to fetch orders:', error.message);
      }
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, filterStatus, filterPaymentStatus, entriesPerPage, fetchProductOwnerMap, fetchSellerIdToOwnerName]);

  useEffect(() => {
    // Wrap in a promise handler to prevent unhandled promise rejections
    fetchOrders().catch((error) => {
      console.error('Unhandled error in fetchOrders:', error);
    });
  }, [currentPage, searchTerm, filterStatus, filterPaymentStatus, entriesPerPage, fetchOrders]);

  useEffect(() => {
    // Wrap in a promise handler to prevent unhandled promise rejections
    fetchOrderStats().catch((error) => {
      console.error('Unhandled error in fetchOrderStats:', error);
    });
  }, []);

  const handleUpdateOrderStatus = async (orderId: string, newStatus: string) => {
    try {
      await api.put(`/admin/orders/${orderId}/status`, { orderStatus: newStatus });
      fetchOrders();
      if (selectedOrder && selectedOrder.id === orderId) {
        const response = await api.get(`/admin/orders/${orderId}`);
        setSelectedOrder(response.data.data);
      }
    } catch (error) {
      console.error('Error updating order status:', error);
    }
  };

  const isCashOnDelivery = (order: Order) =>
    order.paymentMethod === 'CASH_ON_DELIVERY' || !order.activePayment;

  const isOrderPaid = (order: Order) =>
    (order.paymentStatus || order.activePayment?.status) === 'COMPLETED';

  const isOrderDelivered = (order: Order) => order.orderStatus === 'COMPLETE';

  const canRecordPayment = (order: Order) =>
    isOrderDelivered(order) && order.orderStatus !== 'CANCELLED';

  const refreshOrderInView = async (orderId: string) => {
    await fetchOrders();
    await fetchOrderStats();
    if (selectedOrder && (selectedOrder.id === orderId || selectedOrder._id === orderId)) {
      const response = await api.get(`/admin/orders/${orderId}`);
      setSelectedOrder(response.data.data);
    }
  };

  const handleMarkAsPaid = async (orderId: string) => {
    const order =
      orders.find((o) => o.id === orderId || o._id === orderId) ??
      (selectedOrder && (selectedOrder.id === orderId || selectedOrder._id === orderId)
        ? selectedOrder
        : undefined);
    if (order && !canRecordPayment(order)) {
      return;
    }

    const isCashOnDeliveryOrder = order?.paymentMethod === 'CASH_ON_DELIVERY';
    let paymentReference: string | undefined;

    if (!isCashOnDeliveryOrder) {
      const reference = window.prompt(
        t('messages.paymentReferencePrompt', {
          ns: 'orders',
          defaultValue: 'Payment reference (optional, e.g. transaction ID):',
        })
      );
      if (reference === null) return;
      paymentReference = reference.trim() || undefined;
    }

    setMarkingPaidId(orderId);
    try {
      await api.put(`/admin/orders/${orderId}/mark-paid`, {
        ...(paymentReference ? { paymentReference } : {}),
      });
      await refreshOrderInView(orderId);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      alert(err.response?.data?.message || t('messages.paymentStatusUpdateFailed', { ns: 'orders' }));
    } finally {
      setMarkingPaidId(null);
    }
  };

  const handleUpdateOrderPaymentStatus = async (orderId: string, newStatus: string) => {
    const order = orders.find((o) => o.id === orderId || o._id === orderId) ?? selectedOrder;
    if (newStatus === 'COMPLETED' && order && !isOrderDelivered(order)) {
      return;
    }

    try {
      await api.put(`/admin/orders/${orderId}/payment-status`, { paymentStatus: newStatus });
      fetchOrders();
      fetchOrderStats();
      if (selectedOrder && (selectedOrder.id === orderId || selectedOrder._id === orderId)) {
        const response = await api.get(`/admin/orders/${orderId}`);
        setSelectedOrder(response.data.data);
      }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      console.error('Error updating payment status:', error);
      alert(err.response?.data?.message || t('messages.paymentStatusUpdateFailed', { ns: 'orders' }));
    }
  };

  const handleUpdatePaymentStatus = async (orderId: string, newStatus: string) => {
    const order = orders.find((o) => o.id === orderId || o._id === orderId);
    if (newStatus === 'COMPLETED' && order && !isOrderDelivered(order)) {
      return;
    }

    if (order && isCashOnDelivery(order)) {
      return handleUpdateOrderPaymentStatus(orderId, newStatus);
    }

    try {
      if (!order?.activePayment?.id) {
        return handleUpdateOrderPaymentStatus(orderId, newStatus);
      }

      await api.put(`/admin/payments/${order.activePayment.id}/status`, {
        status: newStatus,
      });

      fetchOrders();
      fetchOrderStats();

      if (selectedOrder && selectedOrder.id === orderId) {
        const response = await api.get(`/admin/orders/${orderId}`);
        setSelectedOrder(response.data.data);
      }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      console.error('Error updating payment status:', error);
      alert(err.response?.data?.message || t('messages.paymentStatusUpdateFailed', { ns: 'orders' }));
    }
  };

  const renderPaymentStatusControl = (order: Order, orderId: string) => {
    const paymentStatus = order.paymentStatus || order.activePayment?.status || 'PENDING';
    const paid = isOrderPaid(order);
    const isCancelled = order.orderStatus === 'CANCELLED';

    if (paid) {
      return (
        <div className="flex flex-col gap-1">
          <span
            className={`inline-flex items-center gap-1 text-xs py-1 px-2 rounded-full font-medium w-fit ${getStatusBadgeClass('COMPLETED', 'payment')}`}
          >
            <CheckCircle size={12} />
            {t('paymentStatus.COMPLETED', { ns: 'orders' })}
          </span>
          {order.paymentMarkedPaidAt && (
            <span className="text-xs text-gray-500">
              {t('messages.paidRecordedAt', {
                ns: 'orders',
                date: new Date(order.paymentMarkedPaidAt).toLocaleString(),
              })}
            </span>
          )}
        </div>
      );
    }

    return (
      <div className="flex flex-wrap items-center gap-1">
        <button
          type="button"
          disabled={isCancelled || !canRecordPayment(order) || markingPaidId === orderId}
          onClick={() => handleMarkAsPaid(orderId)}
          title={t('actions.markAsPaid', { ns: 'orders' })}
          className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] leading-tight font-medium rounded bg-green-600 hover:bg-green-700 text-white disabled:opacity-50 whitespace-nowrap"
        >
          <CheckCircle size={11} />
          {markingPaidId === orderId
            ? t('actions.markingAsPaidShort', { ns: 'orders', defaultValue: '…' })
            : t('actions.markAsPaidShort', { ns: 'orders', defaultValue: 'Paid' })}
        </button>
        {order.activePayment && (
          <select
            value={order.activePayment.status}
            onChange={(e) => handleUpdatePaymentStatus(orderId, e.target.value)}
            className={`text-[10px] py-0.5 px-1 rounded border font-medium max-w-[5.5rem] ${getStatusBadgeClass(order.activePayment.status, 'payment')}`}
            title={t('actions.updatePaymentStatus', { ns: 'orders' })}
          >
            <option value="PENDING">{t('paymentStatus.PENDING', { ns: 'orders' })}</option>
            <option value="PROCESSING">{t('paymentStatus.PROCESSING', { ns: 'orders' })}</option>
            <option value="COMPLETED" disabled={!isOrderDelivered(order)}>
              {t('paymentStatus.COMPLETED', { ns: 'orders' })}
            </option>
            <option value="FAILED">{t('paymentStatus.FAILED', { ns: 'orders' })}</option>
            <option value="REFUNDED">{t('paymentStatus.REFUNDED', { ns: 'orders' })}</option>
          </select>
        )}
        {!order.activePayment && paymentStatus !== 'COMPLETED' && (
          <select
            value={paymentStatus}
            onChange={(e) => handleUpdateOrderPaymentStatus(orderId, e.target.value)}
            className={`text-[10px] py-0.5 px-1 rounded border font-medium max-w-[5.5rem] ${getStatusBadgeClass(paymentStatus, 'payment')}`}
          >
            <option value="PENDING">{t('paymentStatus.PENDING', { ns: 'orders' })}</option>
            <option value="FAILED">{t('paymentStatus.FAILED', { ns: 'orders' })}</option>
          </select>
        )}
      </div>
    );
  };

  const handleViewDetails = async (order: Order) => {
    try {
      const orderId = order.id || order._id;
      if (!orderId) {
        console.error('Order ID is missing:', order);
        alert('Unable to fetch order details: Order ID is missing');
        return;
      }
      const response = await api.get(`/admin/orders/${orderId}`);
      const detail = response.data?.data ?? response.data;
      // Keep seller/shop from list (we may have derived it from products API) if detail doesn't have it
      setSelectedOrder({
        ...detail,
        id: detail?.id ?? detail?._id ?? orderId,
        seller_id: detail?.seller_id ?? order.seller_id,
        seller_name: detail?.seller_name ?? order.seller_name,
        shop_name: detail?.shop_name ?? order.shop_name,
        shop_logo: detail?.shop_logo ?? order.shop_logo,
        seller_email: detail?.seller_email ?? order.seller_email,
      });
      setShowDetailsModal(true);
    } catch (error) {
      console.error('Error fetching order details:', error);
      alert('Failed to fetch order details. Please try again.');
    }
  };

  const getStatusBadgeClass = (status: string, type: 'order' | 'payment') => {
    if (type === 'order') {
      switch (status) {
        case 'PENDING': return 'badge-yellow';
        case 'CONFIRMED': return 'bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium';
        case 'OUT_FOR_DELIVERY': return 'bg-purple-100 text-purple-800 px-2 py-1 rounded-full text-xs font-medium';
        case 'COMPLETE': return 'badge-green';
        case 'CANCELLED': return 'badge-red';
        default: return 'badge-gray';
      }
    } else {
      switch (status) {
        case 'PENDING': return 'badge-yellow';
        case 'PROCESSING': return 'bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium';
        case 'COMPLETED': return 'badge-green';
        case 'FAILED': return 'badge-red';
        case 'REFUNDED': return 'bg-orange-100 text-orange-800 px-2 py-1 rounded-full text-xs font-medium';
        default: return 'badge-gray';
      }
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatPrice = (price: number) => {
    return `$${price.toFixed(2)}`;
  };

  const handleExportToExcel = async () => {
    try {
      const response = await api.get('/admin/orders/export', {
        responseType: 'blob',
        params: {
          ...(searchTerm && { search: searchTerm }),
          ...(filterStatus && { status: filterStatus }),
          ...(filterPaymentStatus && { paymentStatus: filterPaymentStatus }),
        }
      });
      
      // Create blob URL and download
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `orders-${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      
      // Use setTimeout to ensure the click happens after the element is added
      setTimeout(() => {
        link.click();
        // Clean up after a short delay
        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }, 100);
      }, 0);
    } catch (error: any) {
      console.error('Error exporting orders:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Error exporting orders. Please try again.';
      alert(errorMessage);
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">{t('title', { ns: 'orders' })}</h1>
        <p className="text-gray-600 mt-2">{t('subtitle', { ns: 'orders' })}</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Orders */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <ShoppingCart className="w-6 h-6 text-green-600" />
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">{t('stats.totalOrders', { ns: 'dashboard' })}</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalOrders}</p>
            </div>
          </div>
        </div>

        {/* Processing */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">{t('stats.processing', { ns: 'dashboard' })}</p>
              <p className="text-2xl font-bold text-gray-900">{stats.processingOrders}</p>
            </div>
          </div>
        </div>

        {/* Completed */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">{t('stats.completed', { ns: 'dashboard' })}</p>
              <p className="text-2xl font-bold text-gray-900">{stats.completedOrders}</p>
            </div>
          </div>
        </div>

        {/* Cancelled */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <XCircle className="w-6 h-6 text-red-600" />
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">{t('stats.cancelled', { ns: 'dashboard' })}</p>
              <p className="text-2xl font-bold text-gray-900">{stats.cancelledOrders}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            onClick={handleExportToExcel}
            variant="outline"
            icon={Download}
            size="sm"
          >
            Excel
          </Button>
        </div>
        
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-700">{t('table.show', { ns: 'common' })}</span>
            <Select
              value={entriesPerPage}
              onChange={(e) => {
                setEntriesPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              options={[
                { value: 10, label: '10' },
                { value: 25, label: '25' },
                { value: 50, label: '50' },
                { value: 100, label: '100' }
              ]}
              className="text-sm w-20"
            />
            <span className="text-sm text-gray-700">{t('table.entries', { ns: 'common' })}</span>
          </div>

          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-700">{t('table.search', { ns: 'common' })}</span>
            <Input
              type="text"
              placeholder={t('search', { ns: 'orders' })}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-64"
            />
          </div>
        </div>
      </div>


      {/* Orders Table - Desktop */}
      <div className="card hidden lg:block">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('fields.orderNumber', { ns: 'orders' })}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('fields.customer', { ns: 'orders' })}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('fields.sellerShop', { ns: 'orders' })}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('fields.orderStatus', { ns: 'orders' })}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('fields.paymentStatus', { ns: 'orders' })}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('fields.totalAmount', { ns: 'orders' })}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('fields.createdAt', { ns: 'orders' })}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('table.actions', { ns: 'common' })}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto"></div>
                  </td>
                </tr>
              ) : orders.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center text-gray-500">
                    {t('table.noData', { ns: 'common' })}
                  </td>
                </tr>
              ) : (
                orders.map((order) => {
                  const orderId = order.id || (order as any)._id || (order as any).id;
                  return (
                  <tr key={orderId || order.orderNumber} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">#{order.orderNumber}</div>
                        <div className="text-sm text-gray-500">{order.items?.length || 0} {t('fields.items', { ns: 'orders' })}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {order.user?.firstName || ''} {order.user?.lastName || ''}
                        </div>
                        <div className="text-sm text-gray-500">{order.user?.email || ''}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {order.shop_logo ? (
                          <img
                            src={order.shop_logo}
                            alt={order.shop_name || 'Shop'}
                            className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                          />
                        ) : (order.shop_name || order.seller_name) ? (
                          <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0 text-gray-600 text-xs font-medium">
                            <Store size={14} />
                          </div>
                        ) : null}
                        <div>
                          <div className="text-sm font-medium text-gray-900">{order.shop_name || order.seller_name || '—'}</div>
                          <div className="text-xs text-gray-500">{order.seller_name && order.shop_name ? order.seller_name : ''}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <select
                        value={order.orderStatus}
                        onChange={(e) => handleUpdateOrderStatus(orderId, e.target.value)}
                        className={`text-xs py-1 px-2 rounded-full border-0 font-medium ${getStatusBadgeClass(order.orderStatus, 'order')}`}
                      >
                        <option value="PENDING">{t('status.PENDING', { ns: 'orders' })}</option>
                        <option value="CONFIRMED">{t('status.CONFIRMED', { ns: 'orders' })}</option>
                        <option value="OUT_FOR_DELIVERY">{t('status.OUT_FOR_DELIVERY', { ns: 'orders' })}</option>
                        <option value="COMPLETE">{t('status.COMPLETE', { ns: 'orders' })}</option>
                      </select>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {renderPaymentStatusControl(order, orderId)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {formatPrice(order.finalPrice)}
                      </div>
                      <div className="text-xs text-gray-500">
                        + {formatPrice(order.shippingFee)} {t('fields.shippingFee', { ns: 'orders' })}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(order.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleViewDetails(order)}
                        className="text-primary-600 hover:text-primary-700 p-1"
                      >
                        <Eye size={16} />
                      </button>
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-4 md:px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-sm text-gray-500 text-center sm:text-left">
              {t('table.showing', { ns: 'common' })} {((currentPage - 1) * entriesPerPage) + 1} {t('table.to', { ns: 'common' })} {Math.min(currentPage * entriesPerPage, total)} {t('table.of', { ns: 'common' })} {total} {t('table.results', { ns: 'common' })}
            </div>
            <div className="flex items-center space-x-2">
              <Button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                variant="outline"
                size="sm"
              >
                {t('table.previous', { ns: 'common' })}
              </Button>
              <span className="text-sm text-gray-700">
                {t('table.page', { ns: 'common' })} {currentPage} {t('table.of', { ns: 'common' })} {totalPages}
              </span>
              <Button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                variant="outline"
                size="sm"
              >
                {t('table.next', { ns: 'common' })}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Orders Cards - Mobile */}
      <div className="lg:hidden space-y-4">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
          </div>
        ) : orders.length === 0 ? (
          <div className="card p-12 text-center">
            <Package size={48} className="mx-auto text-gray-400 mb-4" />
            <p className="text-gray-500">{t('table.noData', { ns: 'common' })}</p>
          </div>
        ) : (
          orders.map((order) => {
            const orderId = order.id || (order as any)._id || (order as any).id;
            return (
              <div key={orderId || order.orderNumber} className="card p-4 space-y-4">
                {/* Header */}
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900">#{order.orderNumber}</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      {order.user?.firstName} {order.user?.lastName}
                    </p>
                    <p className="text-xs text-gray-400">{order.user?.email}</p>
                    {(order.shop_name || order.seller_name) && (
                      <div className="flex items-center gap-2 mt-2">
                        {order.shop_logo ? (
                          <img src={order.shop_logo} alt="" className="w-6 h-6 rounded-full object-cover" />
                        ) : (
                          <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                            <Store size={12} className="text-gray-500" />
                          </div>
                        )}
                        <span className="text-xs text-gray-600">{order.shop_name || order.seller_name}</span>
                        {order.seller_name && order.shop_name && order.seller_name !== order.shop_name && (
                          <span className="text-xs text-gray-500">({order.seller_name})</span>
                        )}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => handleViewDetails(order)}
                    className="text-primary-600 hover:text-primary-700 p-2"
                  >
                    <Eye size={20} />
                  </button>
                </div>

                {/* Order Items Preview */}
                {order.items && order.items.length > 0 && (
                  <div className="border-t border-gray-200 pt-3">
                    <p className="text-xs text-gray-500 mb-2">{order.items.length} {t('fields.items', { ns: 'orders' })}</p>
                    <div className="space-y-2">
                      {order.items.slice(0, 2).map((item, idx) => (
                        <div key={idx} className="flex items-center space-x-2 text-sm">
                          {item.product?.images?.[0] && (
                            <img
                              src={item.product.images[0]}
                              alt={item.product.name}
                              className="w-10 h-10 rounded object-cover"
                            />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 truncate">{item.product?.name}</p>
                            <p className="text-xs text-gray-500">
                              Qty: {item.quantity} × {formatPrice(item.price || 0)}
                            </p>
                          </div>
                        </div>
                      ))}
                      {order.items.length > 2 && (
                        <p className="text-xs text-gray-500">+{order.items.length - 2} more items</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Status & Payment */}
                <div className="grid grid-cols-2 gap-3 border-t border-gray-200 pt-3">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Order Status</p>
                    <select
                      value={order.orderStatus}
                      onChange={(e) => handleUpdateOrderStatus(orderId, e.target.value)}
                      className={`text-xs py-1.5 px-2 rounded-full border-0 font-medium w-full ${getStatusBadgeClass(order.orderStatus, 'order')}`}
                    >
                      <option value="PENDING">{t('status.PENDING', { ns: 'orders' })}</option>
                      <option value="CONFIRMED">{t('status.CONFIRMED', { ns: 'orders' })}</option>
                      <option value="OUT_FOR_DELIVERY">{t('status.OUT_FOR_DELIVERY', { ns: 'orders' })}</option>
                      <option value="COMPLETE">{t('status.COMPLETE', { ns: 'orders' })}</option>
                    </select>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Payment</p>
                    {renderPaymentStatusControl(order, orderId)}
                  </div>
                </div>

                {/* Amount & Date */}
                <div className="flex items-center justify-between border-t border-gray-200 pt-3">
                  <div>
                    <p className="text-xs text-gray-500">Total Amount</p>
                    <p className="text-lg font-semibold text-gray-900">{formatPrice(order.finalPrice)}</p>
                    <p className="text-xs text-gray-400">
                      + {formatPrice(order.shippingFee)} shipping
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-500">Date</p>
                    <p className="text-sm text-gray-700">{formatDate(order.createdAt)}</p>
                  </div>
                </div>

                {/* Payment Method - Simplified */}
                <div className="border-t border-gray-200 pt-3">
                  <p className="text-xs text-gray-500">Payment Method</p>
                  <p className="text-sm font-medium text-gray-900">Payment on Delivery</p>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Order Details Modal */}
      {showDetailsModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">
                {t('orderDetails', { ns: 'orders' })} - #{selectedOrder.orderNumber}
              </h3>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
              {/* Customer & Shipping Info */}
              <div className="space-y-6">
                <div className="card p-4">
                  <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                    <User size={16} className="mr-2" />
                    {t('modal.customerInfo', { ns: 'orders' })}
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="font-medium">{t('modal.name', { ns: 'orders' })}:</span> {selectedOrder.user.firstName} {selectedOrder.user.lastName}
                    </div>
                    <div>
                      <span className="font-medium">{t('modal.email', { ns: 'orders' })}:</span> {selectedOrder.user.email}
                    </div>
                  </div>
                </div>

                {/* Seller & Shop Information */}
                {(selectedOrder.seller_id || selectedOrder.seller_name || selectedOrder.shop_name) && (
                  <div className="card p-4">
                    <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                      <Store size={16} className="mr-2" />
                      {t('modal.sellerAndShop', { ns: 'orders' })}
                    </h4>
                    <div className="flex items-start gap-3">
                      {selectedOrder.shop_logo ? (
                        <img
                          src={selectedOrder.shop_logo}
                          alt={selectedOrder.shop_name || 'Shop'}
                          className="w-12 h-12 rounded-full object-cover flex-shrink-0"
                        />
                      ) : (selectedOrder.shop_name || selectedOrder.seller_name) ? (
                        <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0 text-gray-500">
                          <Store size={24} />
                        </div>
                      ) : null}
                      <div className="space-y-2 text-sm min-w-0">
                        {selectedOrder.seller_name && (
                          <div>
                            <span className="font-medium">{t('modal.sellerName', { ns: 'orders' })}:</span>{' '}
                            {selectedOrder.seller_name}
                          </div>
                        )}
                        {selectedOrder.seller_email && (
                          <div>
                            <span className="font-medium">{t('modal.email', { ns: 'orders' })}:</span>{' '}
                            {selectedOrder.seller_email}
                          </div>
                        )}
                        {selectedOrder.shop_name && (
                          <div>
                            <span className="font-medium">{t('modal.shopName', { ns: 'orders' })}:</span>{' '}
                            {selectedOrder.shop_name}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <div className="card p-4">
                  <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                    <MapPin size={16} className="mr-2" />
                    {t('modal.shippingAddress', { ns: 'orders' })}
                  </h4>
                  <div className="text-sm text-gray-600">
                    <div>{selectedOrder.shippingAddress.fullName}</div>
                    <div>{selectedOrder.shippingAddress.phoneNumber}</div>
                    <div>{selectedOrder.shippingAddress.homeAddress}</div>
                    <div>
                      {selectedOrder.shippingAddress.city}, {selectedOrder.shippingAddress.state} {selectedOrder.shippingAddress.postalCode}
                    </div>
                    <div>{selectedOrder.shippingAddress.country}</div>
                  </div>
                </div>

                {/* Payouts Section - Show when order is COMPLETE */}
                {selectedOrder.orderStatus === 'COMPLETE' && selectedOrder.payouts && selectedOrder.payouts.length > 0 && (
                  <div className="card p-4 bg-green-50 border border-green-200">
                    <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                      <Download size={16} className="mr-2" />
                      Seller Payouts
                    </h4>
                    <div className="space-y-2 text-sm">
                      {selectedOrder.payouts.map((payout, index) => (
                        <div key={payout._id || payout.id || `payout-${index}`} className="p-2 bg-white rounded border">
                          <div className="flex justify-between items-start">
                            <div>
                              <div className="font-medium text-gray-900">
                                Amount: {formatPrice(payout.amount_paid)}
                              </div>
                              <div className="text-xs text-gray-500 mt-1">
                                Status: <span className={`font-medium ${
                                  payout.status === 'PROCESSED' ? 'text-green-600' :
                                  payout.status === 'PENDING' ? 'text-yellow-600' :
                                  'text-red-600'
                                }`}>
                                  {payout.status}
                                </span>
                              </div>
                              {payout.payout_date && (
                                <div className="text-xs text-gray-500">
                                  Date: {new Date(payout.payout_date).toLocaleDateString()}
                                </div>
                              )}
                              {payout.transaction_id && (
                                <div className="text-xs text-gray-500">
                                  Transaction: {payout.transaction_id}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="card p-4">
                  <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                    <CreditCard size={16} className="mr-2" />
                    {t('modal.paymentInfo', { ns: 'orders' })}
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="font-medium">{t('modal.method', { ns: 'orders' })}:</span>
                      <span className="ml-2">
                        {t(`paymentMethods.${selectedOrder.paymentMethod}`, {
                          ns: 'orders',
                          defaultValue: selectedOrder.paymentMethod,
                        })}
                      </span>
                    </div>
                    {selectedOrder.activePayment ? (
                      <>
                        {selectedOrder.paymentMethod !== 'CASH_ON_DELIVERY' && (
                          <>
                            <div>
                              <span className="font-medium">{t('modal.transactionId', { ns: 'orders' })}:</span>{' '}
                              {selectedOrder.activePayment.transactionId}
                            </div>
                            {selectedOrder.activePayment.providerTransactionId && (
                              <div>
                                <span className="font-medium">{t('modal.providerId', { ns: 'orders' })}:</span>{' '}
                                {selectedOrder.activePayment.providerTransactionId}
                              </div>
                            )}
                          </>
                        )}
                        <div>
                          <span className="font-medium">{t('modal.amount', { ns: 'orders' })}:</span>{' '}
                          {formatPrice(selectedOrder.activePayment.amount)}
                        </div>
                      </>
                    ) : null}
                    <div>
                      <span className="font-medium">{t('modal.status', { ns: 'orders' })}:</span>
                      <span className="ml-2 inline-block mt-1">
                        {renderPaymentStatusControl(
                          selectedOrder,
                          selectedOrder.id || selectedOrder._id || ''
                        )}
                      </span>
                      {!isOrderPaid(selectedOrder) && canRecordPayment(selectedOrder) && (
                        <p className="text-xs text-gray-500 mt-2">
                          {t('messages.markPaidInstructions', { ns: 'orders' })}
                        </p>
                      )}
                    </div>
                    {selectedOrder.paymentMethod !== 'CASH_ON_DELIVERY' &&
                      selectedOrder.paymentReference && (
                      <div>
                        <span className="font-medium">{t('modal.reference', { ns: 'orders' })}:</span>{' '}
                        {selectedOrder.paymentReference}
                      </div>
                    )}
                    {selectedOrder.payments && selectedOrder.payments.length > 1 && (
                      <div>
                        <span className="font-medium">{t('modal.paymentAttempts', { ns: 'orders' })}:</span> {selectedOrder.payments.length}
                        <button
                          onClick={() => alert('View all payment attempts feature coming soon!')}
                          className="ml-2 text-primary-600 hover:text-primary-700 text-xs underline"
                        >
                          {t('modal.viewAll', { ns: 'orders' })}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Order Items & Summary */}
              <div className="space-y-6">
                <div className="card p-4">
                  <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                    <Package size={16} className="mr-2" />
                    {t('modal.orderItems', { ns: 'orders' })}
                  </h4>
                  <div className="space-y-3">
                    {selectedOrder.items?.map((item, index) => (
                      <div key={item._id || item.id || `item-${index}`} className="flex items-center space-x-3 p-2 border rounded">
                        <div className="w-12 h-12 bg-gray-100 rounded flex-shrink-0">
                          {item.product.images && item.product.images.length > 0 ? (
                            <img
                              src={item.product.images[0]}
                              alt={item.product.name}
                              className="w-full h-full object-cover rounded"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Package size={16} className="text-gray-400" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate">
                            {item.product?.name || 'Product'}
                          </div>
                          <div className="text-xs text-gray-500">
                            {t('modal.quantity', { ns: 'orders' })}: {item.quantity} × {formatPrice(item.price)}
                            {item.size && ` • ${t('modal.size', { ns: 'orders' })}: ${item.size}`}
                            {item.color && ` • ${t('modal.color', { ns: 'orders' })}: ${item.color}`}
                          </div>
                        </div>
                        <div className="text-sm font-medium text-gray-900">
                          {formatPrice(item.quantity * item.price)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="card p-4">
                  <h4 className="font-medium text-gray-900 mb-3">{t('modal.orderSummary', { ns: 'orders' })}</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>{t('modal.subtotal', { ns: 'orders' })}:</span>
                      <span>{formatPrice(selectedOrder.totalPrice)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>{t('modal.shipping', { ns: 'orders' })}:</span>
                      <span>{formatPrice(selectedOrder.shippingFee)}</span>
                    </div>
                    <div className="flex justify-between font-medium text-base border-t pt-2">
                      <span>{t('modal.total', { ns: 'orders' })}:</span>
                      <span>{formatPrice(selectedOrder.finalPrice)}</span>
                    </div>
                  </div>
                </div>

                <div className="card p-4">
                  <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                    <Calendar size={16} className="mr-2" />
                    {t('modal.orderTimeline', { ns: 'orders' })}
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="font-medium">{t('modal.created', { ns: 'orders' })}:</span> {formatDate(selectedOrder.createdAt)}
                    </div>
                    <div>
                      <span className="font-medium">{t('modal.lastUpdated', { ns: 'orders' })}:</span> {formatDate(selectedOrder.updatedAt)}
                    </div>
                    <div>
                      <span className="font-medium">{t('modal.status', { ns: 'orders' })}:</span>
                      <span className={`ml-2 ${getStatusBadgeClass(selectedOrder.orderStatus, 'order')}`}>
                        {t(`status.${selectedOrder.orderStatus}`, { ns: 'orders' })}
                      </span>
                    </div>
                  </div>
                  {selectedOrder.notes && (
                    <div className="mt-3 p-2 bg-gray-50 rounded">
                      <span className="font-medium">{t('modal.notes', { ns: 'orders' })}:</span>
                      <p className="text-sm text-gray-600 mt-1">{selectedOrder.notes}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Orders;
